import {
  PERMISSIONS,
  appModule,
  systemRoleKey,
  type AccessRole,
  type AccessScopeType,
  type AppModule,
  type Permission,
  type SystemRoleKey,
} from '@overseer/contracts';

type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * Labels as fixed maps of literal keys, so `pnpm i18n:check` sees every one; an
 * interpolated key would reach the screen raw when a translation is missing.
 */
export const ROLE_LABEL: Record<SystemRoleKey, string> = {
  admin: 'admin.role.admin',
  hr_manager: 'admin.role.hr_manager',
  hr_administrator: 'admin.role.hr_administrator',
  hr_viewer: 'admin.role.hr_viewer',
  department_supervisor: 'admin.role.department_supervisor',
  gastronomy_manager: 'admin.role.gastronomy_manager',
  gastronomy_viewer: 'admin.role.gastronomy_viewer',
  catering_manager: 'admin.role.catering_manager',
  catering_site_manager: 'admin.role.catering_site_manager',
  catering_viewer: 'admin.role.catering_viewer',
  hotel_manager: 'admin.role.hotel_manager',
  hotel_viewer: 'admin.role.hotel_viewer',
  cashier_admin: 'admin.role.cashier_admin',
  cash_desk_manager: 'admin.role.cash_desk_manager',
  main_cashier: 'admin.role.main_cashier',
  cashier: 'admin.role.cashier',
  cashier_viewer: 'admin.role.cashier_viewer',
};

export const PERMISSION_LABEL: Record<Permission, string> = {
  'admin.users': 'admin.permission.adminUsers',
  'admin.settings': 'admin.permission.adminSettings',
  'admin.logs': 'admin.permission.adminLogs',
  'hr.read': 'admin.permission.hrRead',
  'hr.employees.read': 'admin.permission.hrEmployeesRead',
  'hr.attendance.correct': 'admin.permission.hrAttendanceCorrect',
  'hr.attendance.correct-past': 'admin.permission.hrAttendanceCorrectPast',
  'hr.employees.write': 'admin.permission.hrEmployeesWrite',
  'hr.history.read': 'admin.permission.hrHistoryRead',
  'hr.costs.read': 'admin.permission.hrCostsRead',
  'hr.reports.read': 'admin.permission.hrReportsRead',
  'hr.payroll.read': 'admin.permission.hrPayrollRead',
  'hr.payroll.write': 'admin.permission.hrPayrollWrite',
  'hr.payroll.close': 'admin.permission.hrPayrollClose',
  'hr.reference.write': 'admin.permission.hrReferenceWrite',
  'hr.recalculate': 'admin.permission.hrRecalculate',
  'hr.readers.manage': 'admin.permission.hrReadersManage',
  'hotel.read': 'admin.permission.hotelRead',
  'hotel.settings': 'admin.permission.hotelSettings',
  'gastronomy.read': 'admin.permission.gastronomyRead',
  'gastronomy.settings': 'admin.permission.gastronomySettings',
  'gastronomy.remarks': 'admin.permission.gastronomyRemarks',
  'catering.read': 'admin.permission.cateringRead',
  'catering.settle': 'admin.permission.cateringSettle',
  'catering.settings': 'admin.permission.cateringSettings',
  'cashier.read': 'admin.permission.cashierRead',
  'cashier.report': 'admin.permission.cashierReport',
  'cashier.accept': 'admin.permission.cashierAccept',
  'cashier.main-desk': 'admin.permission.cashierMainDesk',
  'cashier.overview': 'admin.permission.cashierOverview',
  'cashier.settings': 'admin.permission.cashierSettings',
  'cashier.month-reopen': 'admin.permission.cashierMonthReopen',
  'cashier.accept-own': 'admin.permission.cashierAcceptOwn',
  'cashier.payouts': 'admin.permission.cashierPayouts',
};

/** A scope type as a noun: a column value, a picker's legend. */
export const SCOPE_TYPE_LABEL: Record<AccessScopeType, string> = {
  costCentre: 'admin.scope.costCentre',
  revenueCentre: 'admin.scope.revenueCentre',
  canteen: 'admin.scope.canteen',
  cashDesk: 'admin.scope.cashDesk',
};

/** The two forms of a scope, per type: "every department", "chosen departments". */
export const SCOPE_ALL_LABEL: Record<AccessScopeType, string> = {
  costCentre: 'admin.scope.allCostCentres',
  revenueCentre: 'admin.scope.allRevenueCentres',
  canteen: 'admin.scope.allCanteens',
  cashDesk: 'admin.scope.allCashDesks',
};

export const SCOPE_LIST_LABEL: Record<AccessScopeType, string> = {
  costCentre: 'admin.scope.someCostCentres',
  revenueCentre: 'admin.scope.someRevenueCentres',
  canteen: 'admin.scope.someCanteens',
  cashDesk: 'admin.scope.someCashDesks',
};

export const SCOPE_EMPTY_LABEL: Record<AccessScopeType, string> = {
  costCentre: 'admin.scope.noCostCentres',
  revenueCentre: 'admin.scope.noRevenueCentres',
  canteen: 'admin.scope.noCanteens',
  cashDesk: 'admin.scope.noCashDesks',
};

/** The assignment's field for each scope type. */
export const SCOPE_FIELD = {
  costCentre: 'costCentres',
  revenueCentre: 'revenueCentres',
  canteen: 'canteens',
  cashDesk: 'cashDesks',
} as const satisfies Record<AccessScopeType, string>;

/** A system role by its translated name; a custom role by the name an Admin gave it. */
export const roleLabel = (role: Pick<AccessRole, 'key' | 'name' | 'isSystem'>, t: Translate): string => {
  if (!role.isSystem) return role.name;

  const key = systemRoleKey.safeParse(role.key);

  return key.success ? t(ROLE_LABEL[key.data]) : role.name;
};

/** The role's label from its key, using the options' list for a custom role's name. */
export const roleLabelByKey = (key: string, roles: AccessRole[], t: Translate): string => {
  const role = roles.find((item) => item.key === key);
  if (role) return roleLabel(role, t);

  const system = systemRoleKey.safeParse(key);

  return system.success ? t(ROLE_LABEL[system.data]) : key;
};

/** The applications a set of permissions opens, in `appModule` order. */
export const modulesOfPermissions = (permissions: readonly Permission[]): AppModule[] =>
  appModule.options.filter((module) => permissions.some((key) => PERMISSIONS[key].module === module));
